import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

/**
 * Hook that provides Yup validation schema for the Employee Form
 *
 * There are no password rules and no phone rule: nobody sets another person's password here, and
 * the phone number is read-only on this screen.
 *
 * @returns {Object} Object containing resolver for react-hook-form and the schema
 * @returns {Function} returns.resolver - Yup resolver for react-hook-form
 * @returns {Object} returns.schema - Yup schema object
 */
export const useEmployeeFormSchema = () => {
  const { t } = useTranslation('personnel-employees');

  const schema = useMemo(
    () =>
      yup.object().shape({
        scope: yup.string(),
        roleUuid: yup.string().required(t('form.errors.required')),
        profileId: yup.string().required(t('form.errors.required')),
        firstName: yup.string().trim().max(255, t('form.errors.nameMaxLength')).required(t('form.errors.required')),
        lastName: yup.string().trim().max(255, t('form.errors.lastnameMaxLength')).required(t('form.errors.required')),
        email: yup
          .string()
          .trim()
          .max(500, t('form.errors.maxLength500'))
          .when(['_isEditMode', '_credentialsEnabled'], {
            is: (isEdit: boolean, creds: boolean) => !isEdit && creds,
            then: (schema) => schema.required(t('form.errors.required')).email(t('form.errors.emailInvalid')),
            otherwise: (schema) =>
              schema
                .test('email-optional', t('form.errors.emailInvalid'), (value) => {
                  if (!value || value.trim() === '') return true;
                  return yup.string().email().isValidSync(value);
                })
                .nullable(),
          }),
        phoneNumber: yup.string().trim().nullable(),
        // PIN - Required only if PIN is enabled (based on role permissions)
        posPin: yup
          .string()
          .trim()
          .when('_pinEnabled', {
            is: true,
            then: (schema) =>
              schema
                .required(t('form.errors.required'))
                .test('pin-numeric', t('form.errors.pinNumeric'), (value) => {
                  if (!value) return true;
                  return /^\d+$/.test(value);
                })
                .min(3, t('form.errors.pinLength'))
                .max(6, t('form.errors.pinLength')),
            otherwise: (schema) => schema.nullable(),
          }),
        isActive: yup.boolean(),
        assignedStores: yup
          .array()
          .of(
            yup.object().shape({
              uuid: yup.string().required(),
              name: yup.string().required(),
            }),
          )
          .required(t('form.storesSection.required'))
          .test('at-least-one-store', t('form.storesSection.required'), (value) => {
            return Array.isArray(value) && value.length > 0;
          })
          .min(1, t('form.storesSection.required')),
        allStoresAssigned: yup.boolean(),
        // The notification matrix has no rule to check: its rows come from the backend and every
        // value is a flag the screen already constrains.
        notificationPreferences: yup.object(),
        // Internal fields - not sent to backend
        _credentialsEnabled: yup.boolean().optional(),
        _pinEnabled: yup.boolean().optional(),
        _isEditMode: yup.boolean().optional(),
        _verificationStatus: yup.string().optional(),
        _verificationRequirements: yup.object().optional(),
        _initialEmail: yup.string().optional(),
      }),
    [t],
  );

  const resolver = useMemo(() => yupResolver(schema), [schema]);

  return { resolver, schema };
};
